import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { FaFilter } from 'react-icons/fa';
import { toast } from 'react-toastify';
import reportService from '../services/reportService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ReportsPage = () => {
  const [financialData, setFinancialData] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [topProductsData, setTopProductsData] = useState(null);
  const [topDealersData, setTopDealersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchData = async () => {
      setLoading(true);
      try {
        const [finData, stkData, topProdData, topDealData] = await Promise.all([
          reportService.getFinancialSummary(startDate, endDate),
          reportService.getStockReport(),
          reportService.getTopProducts(startDate, endDate),
          reportService.getTopDealers(startDate, endDate)
        ]);

        // --- Finansal Veri İşleme ---
        // Tüm ayları birleştirip unique (benzersiz) bir liste yapalım
        const allMonths = new Set([
          ...finData.income.map(i => i.month),
          ...finData.expense.map(e => e.month)
        ]);
        const sortedMonths = Array.from(allMonths).sort();

        const incomeDataset = sortedMonths.map(month => {
          const found = finData.income.find(i => i.month === month);
          return found ? found.total : 0;
        });

        const expenseDataset = sortedMonths.map(month => {
          const found = finData.expense.find(e => e.month === month);
          return found ? found.total : 0;
        });

        setFinancialData({
          labels: sortedMonths,
          datasets: [
            {
              label: 'Gelirler',
              data: incomeDataset,
              borderColor: 'rgb(25, 135, 84)',
              backgroundColor: 'rgba(25, 135, 84, 0.5)',
              tension: 0.3
            },
            {
              label: 'Giderler',
              data: expenseDataset,
              borderColor: 'rgb(220, 53, 69)',
              backgroundColor: 'rgba(220, 53, 69, 0.5)',
              tension: 0.3
            },
          ],
        });

        // --- Stok Veri İşleme ---
        setStockData({
          labels: stkData.map(s => s.category_name || 'Kategorisiz'),
          datasets: [
            {
              label: 'Toplam Stok Miktarı',
              data: stkData.map(s => s.total_stock),
              backgroundColor: 'rgba(54, 162, 235, 0.6)',
            },
          ],
        });

        // --- En Çok Satan Ürünler ---
        setTopProductsData({
          labels: topProdData.map(p => p.product_name),
          datasets: [
            {
              label: 'Satış Adedi',
              data: topProdData.map(p => p.total_quantity),
              backgroundColor: 'rgba(255, 159, 64, 0.6)',
            },
          ],
        });

        // --- En İyi Bayiler ---
        setTopDealersData({
          labels: topDealData.map(d => d.dealer_name),
          datasets: [
            {
              label: 'Ciro (TL)',
              data: topDealData.map(d => d.total_revenue),
              backgroundColor: [
                'rgba(255, 99, 132, 0.6)',
                'rgba(54, 162, 235, 0.6)',
                'rgba(255, 206, 86, 0.6)',
                'rgba(75, 192, 192, 0.6)',
                'rgba(153, 102, 255, 0.6)',
              ],
            },
          ],
        });

      } catch (error) {
        toast.error('Rapor verileri alınamadı.');
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = (e) => {
    e.preventDefault();
    fetchData();
  };

  if (loading) return <div>Yükleniyor...</div>;

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-secondary mb-0">Raporlar</h2>
        <form onSubmit={handleFilter} className="d-flex gap-2 align-items-center">
          <input 
            type="date" 
            className="form-control" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)} 
          />
          <span className="text-muted">-</span>
          <input 
            type="date" 
            className="form-control" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)} 
          />
          <button type="submit" className="btn btn-primary d-flex align-items-center">
            <FaFilter className="me-2" /> Filtrele
          </button>
        </form>
      </div>

      <div className="row">
        {/* Finansal Grafik */}
        <div className="col-md-6 mb-4">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">Aylık Gelir / Gider Analizi</h5>
            </div>
            <div className="card-body">
              {financialData && <Line options={{ responsive: true }} data={financialData} />}
            </div>
          </div>
        </div>

        {/* Stok Grafik */}
        <div className="col-md-6 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">Kategori Bazlı Stok</h5>
            </div>
            <div className="card-body">
              {stockData && <Bar options={{ responsive: true }} data={stockData} />}
            </div>
          </div>
        </div>

        {/* En Çok Satan Ürünler */}
        <div className="col-md-6 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">En Çok Satan Ürünler (Bayi)</h5>
            </div>
            <div className="card-body">
              {topProductsData && <Bar options={{ indexAxis: 'y', responsive: true }} data={topProductsData} />}
            </div>
          </div>
        </div>

        {/* En İyi Bayiler */}
        <div className="col-md-6 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">En İyi Bayiler (Ciro)</h5>
            </div>
            <div className="card-body d-flex justify-content-center" style={{ maxHeight: '300px' }}>
              {topDealersData && <Doughnut options={{ responsive: true, maintainAspectRatio: false }} data={topDealersData} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
